import IntersectionObserver from '@core/util/intersection-observer';
import { watch } from 'vue';
import { useIntersectionObserver } from '@vueuse/core';
import useSponsoredData from '../composables/useSponsoredData';

/**
 * when element will be visible
 *
 * @param {HTMLElement | NodeList | array} element
 * @param {function} cb
 * @param {number} [rootMargin=400] margin around the root
 * @param {number} [threshold=10] percentage of the target's visibility the observer's callback should be executed
 * @returns {*}
 */
// eslint-disable-next-line import/prefer-default-export
export const onElementWillBeVisible = (element, cb, rootMargin = 400, threshold = 10) => {
  const isValid = element && cb && typeof cb === 'function';

  if (!isValid) return false;

  // begin: workaround for empty div
  const setMinHeight = (item) => { item.style.minHeight = '1px'; };
  if (Array.isArray(element) || element instanceof NodeList) {
    [...element].forEach((item) => setMinHeight(item));
  } else {
    setMinHeight(element);
  }
  // end: workaround

  const options = {
    rootMargin: `${rootMargin}px`,
    threshold: threshold / 100,
  };
  const io = new IntersectionObserver(() => {
    /* istanbul ignore next */
    io.destroy();
    /* istanbul ignore next */
    cb();
  }, options);

  return io.ready.then(() => io.subscribe(element));
};

export const useLazyLoading = function (sponsoredItemsRef, sponsoredDataParams, rootMargin = 400, percentVisible = 10) {
  const { stop } = useIntersectionObserver(sponsoredItemsRef,
    ([{ isIntersecting }]) => {
      /* istanbul ignore next */
      if (isIntersecting) {
        useSponsoredData(sponsoredDataParams);
        stop();
      }
    },
    {
      rootMargin: `${rootMargin}px`,
      threshold: percentVisible / 100,
    });
};

export const usePDPLazyLoading = function (sponsoredItemTopComp, sponsoredItemsRef, sponsoredDataParams, rootMargin = 400, percentVisible = 10) {
  let xapiCallExecuted = false;
  // set lazy loading for sponsored items
  const { stop } = useIntersectionObserver(sponsoredItemsRef,
    ([{ isIntersecting }]) => {
      /* istanbul ignore next */
      if (isIntersecting && !xapiCallExecuted) {
        sponsoredDataParams.collapsedProductDetails = true;
        useSponsoredData(sponsoredDataParams);
        xapiCallExecuted = true;
        stop();
      }
    },
    {
      rootMargin: `${rootMargin}px`,
      threshold: percentVisible / 100,
    });
  // set lazy loading for sponsored item top
  watch(sponsoredItemTopComp, () => {
    /* istanbul ignore next */
    if (sponsoredItemTopComp.value && !xapiCallExecuted) {
      useSponsoredData(sponsoredDataParams);
      xapiCallExecuted = true;
      stop();
    }
  });
};
